import React, { useState, useRef, useEffect } from "react";
import { Editor, useEditor } from "@craftjs/core"; // Added useEditor back for the EditorActions component
import { Container } from "./components/user/Container";
import { Text } from "./components/user/Text";
import { Button } from "./components/user/Button";
import { Image } from "./components/user/Image";
import { FeatureSection } from "./components/user/FeatureSection";
import { ResponsiveContainer } from "./components/user/ResponsiveContainer";
import { HeaderSection } from "./components/sections/HeaderSection";
import { HeroSection } from "./components/sections/HeroSection";
import { WhyChooseUs } from "./components/sections/WhyChooseUs";
import { TestimonialsSection } from "./components/sections/TestimonialsSection";
import { CTASection } from "./components/sections/CTASection";
import { FooterSection } from "./components/sections/FooterSection";
import { HeaderBar } from "./components/layout/HeaderBar";
import { EditorView } from "./components/layout/EditorView";
import PreviewPage from "./components/preview/PreviewPage";
import { downloadState, loadStateFromFile } from "./utils/craftState";
import "./App.css";
import "./styles/index.css"; // Import our main index.css that manages all component styles
import "./styles/landing.css";
import "./styles/components/preview/PreviewPage.css"; // Import PreviewPage styles

// Define device breakpoints dimensions
export interface DeviceBreakpoint {
  name: string;
  width: number;
  icon: string;
}

// Define available device breakpoints
export const deviceBreakpoints: DeviceBreakpoint[] = [
  { name: "Mobile S", width: 320, icon: "📱" },
  { name: "Mobile M", width: 375, icon: "📱" },
  { name: "Mobile L", width: 425, icon: "📱" },
  { name: "Tablet", width: 768, icon: "📱" },
  { name: "Laptop", width: 1024, icon: "💻" },
  { name: "Laptop L", width: 1440, icon: "💻" },
  { name: "Desktop", width: 1920, icon: "🖥️" },
  { name: "Full Width", width: 2560, icon: "🖥️" },
];

function App() {
  // Check URL parameters on component mount for preview mode
  const [isPreviewRoute, setIsPreviewRoute] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [canvasSize, setCanvasSize] = useState<DeviceBreakpoint>(
    deviceBreakpoints[deviceBreakpoints.length - 1] // Default to Full Width
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Refs to store the uploaded file data
  const jsonFileInputRef = useRef<HTMLInputElement | null>(null);
  const lzPageFileInputRef = useRef<HTMLInputElement | null>(null);

  // State to store imported data and uploaded lzpage file to pass to child components
  const [importData, setImportData] = useState<any>(null);
  const [uploadedLzFile, setUploadedLzFile] = useState<File | null>(null);

  // Check for preview mode in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("preview") === "true") {
      setIsPreviewRoute(true);
    }
  }, []);

  // Function to handle JSON file upload
  const handleJsonFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsLoading(true);
      setErrorMessage(null);
      // Read the file as text
      const text = await file.text();

      // Parse the JSON data
      const jsonData = JSON.parse(text);
      const data = jsonData.data || jsonData; // Support both formats

      // We'll pass this data to the appropriate component that has access to actions
      setImportData(data);

      setIsLoading(false);
      alert("JSON state loaded successfully!");
    } catch (error) {
      console.error("Error loading JSON state:", error);
      setErrorMessage(
        "Failed to load JSON state. Please check the file format."
      );
      setIsLoading(false);
    }

    // Clear the input so the same file can be loaded again if needed
    if (event.target) {
      event.target.value = "";
    }
  };

  // Function to handle .lzpage file upload (compressed format)
  const handleLzPageFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setErrorMessage(null);
    setUploadedLzFile(file);

    // Clear the input so the same file can be loaded again if needed
    if (event.target) {
      event.target.value = "";
    }
  };

  // Function to handle canvas size change
  const handleSetCanvasSize = (breakpoint: DeviceBreakpoint) => {
    setCanvasSize(breakpoint);
  };

  // Define a dummy onExport function for HeaderBar
  const handleExportJSON = () => {
    // This will be handled by the EditorActions component
  };

  // Function to open a new window with the real preview
  const handleOpenPreviewInNewWindow = () => {
    // We need to access the editor query for this from window since we're outside the Editor context
    const editor = (window as any).editor;
    if (!editor) {
      alert("Editor not initialized. Please try again.");
      return;
    }

    // First save the current state to localStorage for the preview to access
    try {
      const json = editor.query.serialize();
      localStorage.setItem("craftjs-demo", JSON.stringify(json));

      // Then open a new window to the preview route
      const previewUrl = `${window.location.origin}${window.location.pathname}?preview=true`;
      const previewWindow = window.open(previewUrl, "_blank");

      if (!previewWindow) {
        alert(
          "Unable to open preview window. Please check if pop-up blockers are enabled."
        );
      }
    } catch (error) {
      console.error("Error preparing preview:", error);
      alert("Error preparing preview. Please see console for details.");
    }
  };

  // If this is the preview route, render the PreviewPage component
  if (isPreviewRoute) {
    return <PreviewPage />;
  }

  return (
    <div className="App">
      <Editor
        resolver={{
          Container,
          Text,
          Button,
          Image,
          FeatureSection,
          ResponsiveContainer,
          HeaderSection,
          HeroSection,
          WhyChooseUs,
          TestimonialsSection,
          CTASection,
          FooterSection,
        }}
        enabled={!showPreview} // Disable editor in preview mode
        onNodesChange={(query) => {
          // Debug info about selection
          const selected = query.getEvent("selected").first();
          if (selected) {
            console.log("Node selected:", selected, query.node(selected).get());
          }

          // Store the editor in the window for debugging and exporting
          (window as any).editor = { query, actions: query };
        }}
        onRender={({ render }) => render}
      >
        <EditorActions
          importData={importData}
          setImportData={setImportData}
          uploadedLzFile={uploadedLzFile}
          setUploadedLzFile={setUploadedLzFile}
          setIsLoading={setIsLoading}
          setErrorMessage={setErrorMessage}
        />
        <HeaderBar
          title="Landing Builder"
          showPreview={showPreview}
          setShowPreview={setShowPreview}
          deviceBreakpoints={deviceBreakpoints}
          currentBreakpoint={canvasSize}
          onBreakpointChange={handleSetCanvasSize}
          onExport={handleExportJSON}
          onOpenPreviewInNewWindow={handleOpenPreviewInNewWindow} // Add the new prop
        />
        <EditorView showPreview={showPreview} canvasSize={canvasSize} />
        {showPreview && (
          <div className="preview-overlay">
            <button
              className="btn-close-preview"
              onClick={() => setShowPreview(false)}
            >
              Close Preview
            </button>
          </div>
        )}
      </Editor>

      {errorMessage && <div className="error-message">{errorMessage}</div>}

      <div className="file-actions-container">
        <div className="file-upload-container">
          <input
            type="file"
            accept=".json"
            onChange={handleJsonFileUpload}
            ref={jsonFileInputRef}
            style={{ display: "none" }}
          />
          <button
            className="btn-upload"
            onClick={() => jsonFileInputRef.current?.click()}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Load JSON"}
          </button>

          <input
            type="file"
            accept=".lzpage"
            onChange={handleLzPageFileUpload}
            ref={lzPageFileInputRef}
            style={{ display: "none" }}
          />
          <button
            className="btn-upload"
            onClick={() => lzPageFileInputRef.current?.click()}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Load .lzpage"}
          </button>
        </div>

        <div className="file-save-container">
          <button className="btn-save" disabled={isLoading}>
            Save as JSON
          </button>
          <button className="btn-save" disabled={isLoading}>
            Save as .lzpage
          </button>
        </div>
      </div>
    </div>
  );
}

// New component to handle editor actions within Editor context
const EditorActions = ({
  importData,
  setImportData,
  uploadedLzFile,
  setUploadedLzFile,
  setIsLoading,
  setErrorMessage,
}: {
  importData: any;
  setImportData: React.Dispatch<React.SetStateAction<any>>;
  uploadedLzFile: File | null;
  setUploadedLzFile: React.Dispatch<React.SetStateAction<File | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  // Now we can safely use useEditor here as we're inside the Editor context
  const { actions, query } = useEditor();

  // Handle JSON import if data is available
  React.useEffect(() => {
    if (importData) {
      try {
        actions.deserialize(importData);
        setImportData(null); // Clear after using
      } catch (error) {
        console.error("Error deserializing JSON data:", error);
        setErrorMessage("Failed to import JSON data.");
      }
    }
  }, [importData, actions, setErrorMessage]);

  // Handle lzpage file import if available
  React.useEffect(() => {
    const handleLzPageFile = async () => {
      if (uploadedLzFile) {
        try {
          // Create a safe backup of current state
          const backupState = query.serialize();

          // Use the loadStateFromFile function with error handling and recovery
          const success = await loadStateFromFile(uploadedLzFile, actions);

          if (success) {
            setErrorMessage(null);
          } else {
            // If loading failed, restore previous state
            console.error("Error loading compressed state - restoring backup");
            actions.deserialize(backupState);
            setErrorMessage(
              "Failed to load compressed state. Restored previous state."
            );
          }
        } catch (error) {
          console.error("Error loading compressed state:", error);
          setErrorMessage(
            "Error loading compressed state. See console for details."
          );
        } finally {
          setIsLoading(false);
          setUploadedLzFile(null); // Clear after using
        }
      }
    };

    if (uploadedLzFile) {
      handleLzPageFile();
    }
  }, [
    uploadedLzFile,
    actions,
    query,
    setErrorMessage,
    setIsLoading,
    setUploadedLzFile,
  ]);

  // Add event handlers for export/save to window
  React.useEffect(() => {
    // Add export JSON functionality to save button
    const saveJsonBtn = document.querySelector(
      ".file-save-container .btn-save:nth-child(1)"
    );

    const handleExportJSONLocal = () => {
      try {
        // Get serialized data
        const json = query.serialize();

        // Add metadata about available components in the resolver
        const exportData = {
          version: "1.0.0",
          availableComponents: [
            "Container",
            "Text",
            "Button",
            "Image",
            "FeatureSection",
            "ResponsiveContainer",
            "HeaderSection",
            "HeroSection",
            "WhyChooseUs",
            "TestimonialsSection",
            "CTASection",
            "FooterSection",
          ],
          data: json,
        };

        // Convert to string with pretty formatting
        const jsonStr = JSON.stringify(exportData, null, 2);

        // Create a blob from the data
        const blob = new Blob([jsonStr], { type: "application/json" });
        const url = URL.createObjectURL(blob);

        // Create download link and trigger click
        const link = document.createElement("a");
        link.download = `landing-page-${
          new Date().toISOString().split("T")[0]
        }.json`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error exporting JSON:", error);
        setErrorMessage("Failed to export JSON. See console for details.");
      }
    };

    if (saveJsonBtn) {
      saveJsonBtn.addEventListener("click", handleExportJSONLocal);
    }

    // Add save as .lzpage functionality to save button
    const saveLzpageBtn = document.querySelector(
      ".file-save-container .btn-save:nth-child(2)"
    );

    const handleSaveCompressedLocal = () => {
      try {
        // Use the downloadState function from craftState.ts
        downloadState(query);
        setErrorMessage(null);
      } catch (error) {
        console.error("Error saving compressed state:", error);
        setErrorMessage(
          "Failed to save compressed state. See console for details."
        );
      }
    };

    if (saveLzpageBtn) {
      saveLzpageBtn.addEventListener("click", handleSaveCompressedLocal);
    }

    // Clean up listeners
    return () => {
      if (saveJsonBtn) {
        saveJsonBtn.removeEventListener("click", handleExportJSONLocal);
      }
      if (saveLzpageBtn) {
        saveLzpageBtn.removeEventListener("click", handleSaveCompressedLocal);
      }
    };
  }, [query, setErrorMessage]);

  // This component doesn't render anything visible
  return null;
};

export default App;
